/**
 * FeedbackThumbs — contextual rating control (ui-patterns.md §1 / F-11).
 *
 * Adapted from ClientSideTools/src/components/ui/FeedbackThumbs.tsx.
 *
 * localStorage keys (document per this repo's convention):
 *   feedbackThumbs:v1:<surfaceId>  — suppress this surface for 30 days after a rating
 *   feedbackQueue:v1               — owned by web/src/lib/feedback/client.ts (retry queue)
 *
 * Privacy: payload must never include list names, item text, or other user content
 * (CUSTOMER_FEEDBACK_PLAN §4.3).
 */

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { fieldCls } from "../AuthShell.js";
import { Button } from "../ui/Button.js";
import { getCaptchaToken } from "../../lib/captcha.js";
import { getElapsedMs, submitIncident } from "../../lib/feedback/client.js";
import { buildFeedbackContext } from "../../lib/feedback/context.js";
import {
  INCIDENT_CATEGORIES,
  type IncidentCategoryId,
  type SurfaceId,
} from "../../lib/feedback/surfaces.js";

const SUPPRESS_PREFIX = "feedbackThumbs:v1:";
const SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_REASON = 5000;
const MAX_EMAIL = 200;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface FeedbackThumbsProps {
  surfaceId: SurfaceId;
  /** Allowlisted non-sensitive facts only — never list names or item text. */
  payload?: Record<string, unknown>;
  className?: string;
}

function isSuppressed(surfaceId: string): boolean {
  try {
    const raw = localStorage.getItem(SUPPRESS_PREFIX + surfaceId);
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until) || Date.now() > until) {
      localStorage.removeItem(SUPPRESS_PREFIX + surfaceId);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function suppress(surfaceId: string): void {
  try {
    localStorage.setItem(SUPPRESS_PREFIX + surfaceId, String(Date.now() + SUPPRESS_MS));
  } catch {
    // ignore quota / private mode
  }
}

type Phase = "idle" | "thanks" | "down-reason";

export function FeedbackThumbs({ surfaceId, payload, className }: FeedbackThumbsProps) {
  const reasonId = useId();
  const emailId = useId();
  const [hidden, setHidden] = useState(true);
  const [phase, setPhase] = useState<Phase>("idle");
  const [pressed, setPressed] = useState<"up" | "down" | null>(null);
  const [liveMessage, setLiveMessage] = useState("");
  const [category, setCategory] = useState<IncidentCategoryId | null>(null);
  const [reason, setReason] = useState("");
  const [email, setEmail] = useState("");
  const [reasonBusy, setReasonBusy] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setHidden(isSuppressed(surfaceId));
    setPhase("idle");
    setPressed(null);
    setLiveMessage("");
    setCategory(null);
    setReason("");
    setEmail("");
  }, [surfaceId]);

  useEffect(() => {
    if (phase === "down-reason") {
      textareaRef.current?.focus();
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== "down-reason") return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setPhase("thanks");
        setLiveMessage("Thanks!");
        suppress(surfaceId);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [phase, surfaceId]);

  async function sendRating(
    rating: "up" | "down",
    extras?: {
      category?: string;
      body?: string;
      contactEmail?: string;
    },
  ) {
    const body = extras?.body?.trim() || (rating === "up" ? "Thumbs up" : "Thumbs down");
    let captchaToken: string | undefined;
    try {
      captchaToken = await getCaptchaToken("feedback_rating");
    } catch {
      captchaToken = undefined;
    }
    const context = buildFeedbackContext({ surfaceId, payload });
    await submitIncident({
      appId: context.appId,
      type: "rating",
      rating,
      ...(extras?.category ? { category: extras.category } : {}),
      body,
      ...(extras?.contactEmail ? { contactEmail: extras.contactEmail } : {}),
      context,
      hp: "",
      elapsedMs: getElapsedMs(),
      ...(captchaToken ? { captchaToken } : {}),
    });
  }

  function onThumb(rating: "up" | "down") {
    if (phase !== "idle") return;
    setPressed(rating);
    // Submit immediately — abandoned down-reason still leaves a usable signal.
    void sendRating(rating);
    if (rating === "up") {
      setPhase("thanks");
      setLiveMessage("Thanks!");
      suppress(surfaceId);
    } else {
      setPhase("down-reason");
      setLiveMessage("Thanks — optional details below.");
    }
  }

  async function onReasonSubmit(e: FormEvent) {
    e.preventDefault();
    if (reasonBusy) return;
    const trimmedEmail = email.trim();
    if (trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)) return;
    const trimmedReason = reason.trim();
    const hasExtra = Boolean(category || trimmedReason || trimmedEmail);
    if (hasExtra) {
      setReasonBusy(true);
      try {
        await sendRating("down", {
          ...(category ? { category } : {}),
          body: trimmedReason || "Thumbs down",
          ...(trimmedEmail ? { contactEmail: trimmedEmail } : {}),
        });
      } finally {
        setReasonBusy(false);
      }
    }
    setPhase("thanks");
    setLiveMessage("Thanks!");
    suppress(surfaceId);
  }

  function dismissReason() {
    setPhase("thanks");
    setLiveMessage("Thanks!");
    suppress(surfaceId);
  }

  if (hidden) return null;

  return (
    <div className={["mt-4", className].filter(Boolean).join(" ")}>
      {/* Live region must exist before confirmation text (ui-patterns.md §1 / Toast.tsx). */}
      <div className="sr-only" aria-live="polite">
        {liveMessage}
      </div>

      {phase === "thanks" ? (
        <p className="text-sm text-muted" role="status">
          Thanks!
        </p>
      ) : null}

      {phase === "idle" ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted">Was this helpful?</span>
          <button
            type="button"
            aria-label="This was helpful"
            aria-pressed={pressed === "up"}
            onClick={() => onThumb("up")}
            className={
              "inline-flex h-11 w-11 items-center justify-center rounded-lg border border-line " +
              "bg-white text-ink transition hover:bg-slate-50 " +
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
            }
          >
            <ThumbIcon up filled={pressed === "up"} />
          </button>
          <button
            type="button"
            aria-label="This wasn't helpful"
            aria-pressed={pressed === "down"}
            onClick={() => onThumb("down")}
            className={
              "inline-flex h-11 w-11 items-center justify-center rounded-lg border border-line " +
              "bg-white text-ink transition hover:bg-slate-50 " +
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
            }
          >
            <ThumbIcon up={false} filled={pressed === "down"} />
          </button>
        </div>
      ) : null}

      {phase === "down-reason" ? (
        <form
          onSubmit={(e) => void onReasonSubmit(e)}
          className="mt-2 max-w-md space-y-3 rounded-lg border border-line bg-slate-50 p-3"
        >
          <p className="text-sm font-medium text-ink">What went wrong?</p>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Reason category">
            {INCIDENT_CATEGORIES.map((c) => {
              const selected = category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setCategory(selected ? null : c.id)}
                  className={
                    "rounded-lg border px-3 py-1.5 text-xs font-medium transition " +
                    (selected
                      ? "border-brand-600 bg-brand-50 text-brand-700"
                      : "border-line bg-white text-ink hover:bg-slate-100") +
                    " focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                  }
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div>
            <label htmlFor={reasonId} className="sr-only">
              Optional details
            </label>
            <textarea
              ref={textareaRef}
              id={reasonId}
              rows={3}
              maxLength={MAX_REASON}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional details"
              className={`${fieldCls} min-h-[4.5rem] resize-y`}
            />
          </div>
          <div>
            <label htmlFor={emailId} className="mb-1 block text-xs text-muted">
              Email (optional) — only if you&apos;d like a reply
            </label>
            <input
              id={emailId}
              type="email"
              name="email"
              maxLength={MAX_EMAIL}
              value={email}
              autoComplete="email"
              inputMode="email"
              onChange={(e) => setEmail(e.target.value)}
              className={fieldCls}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={reasonBusy}>
              {reasonBusy ? "Sending…" : "Submit"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={dismissReason}>
              Skip
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

function ThumbIcon({ up, filled }: { up: boolean; filled: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={up ? undefined : "rotate-180"}
    >
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  );
}
